import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { duplicatesApi } from '../services/api';

function Duplicates({ user, onLogout }) {
  const [groups, setGroups] = useState({ byName: [], byPhone: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [merging, setMerging] = useState(false);
  const [mergeModal, setMergeModal] = useState(null);
  const [keepId, setKeepId] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await duplicatesApi.find();
      setGroups(res.data || { byName: [], byPhone: [] });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMerge = (group) => {
    setMergeModal(group);
    setKeepId(group.users[0]?.id);
  };

  const handleMerge = async () => {
    if (!keepId || !mergeModal) return;
    const fromIds = mergeModal.users.filter(u => u.id !== keepId).map(u => u.id);
    if (!window.confirm(`This will merge ${fromIds.length} duplicate(s) into ${mergeModal.users.find(u => u.id === keepId)?.firstName} ${mergeModal.users.find(u => u.id === keepId)?.lastName}. All games and stats will be transferred. This cannot be undone. Continue?`)) return;

    setMerging(true);
    try {
      for (const fromId of fromIds) {
        await duplicatesApi.merge(fromId, keepId);
      }
      setMergeModal(null);
      fetchData();
    } catch (e) {
      setError(e.message || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const renderGroups = (groupList, label) => (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <h3>{label} ({groupList.length})</h3>
      </div>
      {groupList.length === 0 ? (
        <div className="empty-state" style={{ padding: 30 }}>
          <p>No duplicates found</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Match</th>
              <th>Users</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupList.map((g, i) => (
              <tr key={i}>
                <td><strong>{g.key}</strong></td>
                <td>
                  {g.users.map(u => (
                    <div key={u.id} style={{ marginBottom: 4, fontSize: 13 }}>
                      <strong>{u.firstName} {u.lastName}</strong>
                      <span style={{ color: '#888' }}> · {u.phoneNumber || 'no phone'} · {u.username}</span>
                      <span style={{ color: '#aaa', fontSize: 11 }}> · created {new Date(u.createdAt).toLocaleDateString()}</span>
                    </div>
                  ))}
                </td>
                <td>
                  <button className="btn btn-primary btn-sm" onClick={() => handleOpenMerge(g)}>Merge</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h2>Duplicate Players</h2>
            <p>Find and merge users with duplicate names or phone numbers</p>
          </div>
          <button className="btn btn-secondary" onClick={fetchData}>↻ Refresh</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading"><div className="loading-spinner"></div></div>
        ) : (
          <>
            {renderGroups(groups.byName || [], 'Duplicates by Name')}
            {renderGroups(groups.byPhone || [], 'Duplicates by Phone Number')}
          </>
        )}

        {mergeModal && (
          <div className="modal-overlay" onClick={() => setMergeModal(null)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
              <div className="modal-header">
                <h3>Merge Duplicates</h3>
                <button className="modal-close" onClick={() => setMergeModal(null)}>&times;</button>
              </div>
              <div className="modal-body">
                <p>Select which user to <strong>keep</strong>. All others will be merged into this one (game results, points, dealer assignments, etc. will be transferred).</p>
                <div style={{ marginTop: 16 }}>
                  {mergeModal.users.map(u => (
                    <label key={u.id} style={{
                      display: 'block', padding: 12,
                      border: keepId === u.id ? '2px solid #2ecc71' : '1px solid #ddd',
                      borderRadius: 8, marginBottom: 8, cursor: 'pointer',
                      background: keepId === u.id ? '#e8f8f0' : '#fff'
                    }}>
                      <input
                        type="radio"
                        name="keep"
                        value={u.id}
                        checked={keepId === u.id}
                        onChange={() => setKeepId(u.id)}
                        style={{ marginRight: 8 }}
                      />
                      <strong>{u.firstName} {u.lastName}</strong>
                      <div style={{ marginLeft: 24, fontSize: 13, color: '#666' }}>
                        {u.phoneNumber || 'no phone'} · @{u.username}<br />
                        Created: {new Date(u.createdAt).toLocaleDateString()}
                      </div>
                    </label>
                  ))}
                </div>
                <div style={{
                  marginTop: 16, padding: 12, background: '#fff3cd',
                  border: '1px solid #ffc107', borderRadius: 6, fontSize: 13
                }}>
                  ⚠️ This action cannot be undone. {mergeModal.users.length - 1} user(s) will be deleted.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setMergeModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleMerge} disabled={merging || !keepId}>
                  {merging ? 'Merging...' : 'Merge'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Duplicates;
